// Operator usage/cost dashboard (#16) + funnel metrics vs #17 targets.
// Data source is the 6h `usageRollup` snapshot (stats/rollup) via the
// admin-gated `usageSummary` callable — no BQ access from the client.
import { useEffect, useState } from "react";
import { getUsage, type UsageRollup } from "../api";

function pct(x: number | null | undefined): string {
  return x == null ? "—" : `${Math.round(x * 100)}%`;
}

export default function Usage() {
  const [data, setData] = useState<UsageRollup | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    getUsage().then(setData).catch((e) => setErr(e?.code === "permission-denied" ? "not an operator account" : String(e?.message ?? e)));
  }, []);

  if (err) return <p>Usage dashboard: {err}.</p>;
  if (!data) return <p>Loading rollup…</p>;

  const maxKits = Math.max(1, ...data.kits.map((d) => d.kits));
  const spend = Object.entries(
    data.usage.reduce<Record<string, { calls: number; images: number; usd: number }>>((acc, u) => {
      const a = (acc[u.model] ??= { calls: 0, images: 0, usd: 0 });
      a.calls += u.calls; a.images += u.images; a.usd += u.estUsd;
      return acc;
    }, {}),
  ).sort((a, b) => b[1].usd - a[1].usd);
  const totalUsd = spend.reduce((s, [, a]) => s + a.usd, 0);
  const conversion = data.totals.users > 0 ? data.totals.paying / data.totals.users : null;

  return (
    <section>
      <h2>Usage &amp; cost</h2>
      <p className="muted">
        Rollup refreshes every 6h · users {data.totals.users} · paying {data.totals.paying}
        {" "}· ready rate {pct(data.totals.readyRate)} (target {data.targets.photoToReadyPct}%)
        {" "}· free→paid {pct(conversion)} (target {data.targets.freeToPaidPct}%)
        {" "}· est. 90-day cost ${totalUsd.toFixed(2)} (budget $25/mo)
      </p>

      <h3>Kits per day</h3>
      <table>
        <thead><tr><th>day</th><th>kits</th><th>ready</th><th>failed</th><th>in&nbsp;flight</th><th>users</th><th>artifacts</th></tr></thead>
        <tbody>
          {data.kits.map((d) => (
            <tr key={d.day}>
              <td>{d.day}</td>
              <td>
                <span className="bar" style={{ width: `${Math.max(4, (d.kits / maxKits) * 120)}px` }} /> {d.kits}
              </td>
              <td>{d.ready}</td><td>{d.failed}</td><td>{d.inFlight}</td><td>{d.users}</td><td>{d.artifacts}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3>Model spend (90d)</h3>
      <table>
        <thead><tr><th>model</th><th>calls</th><th>images</th><th>est. USD</th></tr></thead>
        <tbody>
          {spend.length === 0 && <tr><td colSpan={4}>no usage rows yet</td></tr>}
          {spend.map(([model, a]) => (
            <tr key={model}><td>{model}</td><td>{a.calls}</td><td>{a.images}</td><td>${a.usd.toFixed(3)}</td></tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
